import React, { FC, useState } from 'react';
import {
  Button,
  EllipsisSpan,
  Paging,
  PopConfirm,
  Table,
  Wrapper,
  message,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiV2InspectionRecorderDeletePost, apiV2InspectionRecorderPagePost } from '@maxtropy/device-customer-apis-v2';
import EditModal from './EditModal';
import styles from './index.module.scss';

interface InspectionRecorderRow {
  id: number;
  deviceName?: string;
  weight?: number;
}

const columns = [
  {
    title: '巡检记录仪设备',
    dataIndex: 'deviceName',
    ellipsis: { showTitle: true },
    render: (value: string) => <EllipsisSpan value={value} />,
  },
  {
    title: '权重',
    dataIndex: 'weight',
    width: 160,
  },
];

const InspectionRecorderConfig: FC = () => {
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [updateKey, triggerUpdate] = useUpdate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number>();

  const { data: pageList, loading } = useRequest(
    async () => {
      const res = await apiV2InspectionRecorderPagePost({
        page: pageOffset,
        size: pageSize,
      });
      setTotalCount(Number(res.total) || 0);
      return (res.list ?? []).filter((item): item is InspectionRecorderRow => item.id != null);
    },
    {
      refreshDeps: [pageOffset, pageSize, updateKey],
    }
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(undefined);
  };

  const handleDelete = async (id: number) => {
    await apiV2InspectionRecorderDeletePost({ id });
    message.success('删除成功');
    triggerUpdate();
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 180,
      render: (_: unknown, record: InspectionRecorderRow) => (
        <Space size={16}>
          <Button
            type="link"
            className={styles.linkBtn}
            onClick={() => {
              setEditingId(record.id);
              setModalOpen(true);
            }}
          >
            编辑
          </Button>
          <PopConfirm title="删除后不可恢复，请确认是否删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger className={styles.linkBtn}>
              删除
            </Button>
          </PopConfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '配置' }]} className={styles.wrapper}>
        <div className={styles.toolbar}>
          <div className={styles.actionGroup}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(undefined);
                setModalOpen(true);
              }}
            >
              新增巡检记录仪
            </Button>
          </div>
        </div>

        <Table rowKey="id" loading={loading} columns={buildColumns} dataSource={pageList ?? []} pagination={false} />
        <div className={styles.footerBar}>
          <Paging pagingInfo={pagingInfo} />
        </div>

        <Button onClick={() => navigate('/monitor/video/inspectionView')}>返回</Button>
      </Wrapper>

      <EditModal
        open={modalOpen}
        recorderId={editingId}
        onCancel={closeModal}
        onSuccess={isCreate => {
          if (isCreate) {
            setPageOffset(1);
          }
          closeModal();
          triggerUpdate();
        }}
      />
    </>
  );
};

export default InspectionRecorderConfig;
